/**
 * Client-side observability: forwards unhandled errors and unhandled promise
 * rejections to the API's /api/observability/client-errors endpoint.
 *
 * Failures are silently swallowed — we never want the error reporter to
 * itself break the page.
 */

const BASE_URL = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
const ENDPOINT = `${BASE_URL}/api/observability/client-errors`;

interface ClientErrorPayload {
  message: string;
  stack?: string | null;
  url?: string;
  userAgent?: string;
  line?: number | null;
  column?: number | null;
  componentStack?: string | null;
  level?: "error" | "warn" | "fatal";
}

// In-memory de-dupe within a short window so a runaway re-render storm doesn't
// flood the server with thousands of identical reports.
const recentReports = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5_000;

function shouldReport(key: string): boolean {
  const now = Date.now();
  for (const [k, t] of recentReports) {
    if (now - t > DEDUPE_WINDOW_MS) recentReports.delete(k);
  }
  if (recentReports.has(key)) return false;
  recentReports.set(key, now);
  return true;
}

export async function reportClientError(payload: ClientErrorPayload): Promise<void> {
  const key = `${payload.message}|${payload.stack?.slice(0, 200) ?? ""}`;
  if (!shouldReport(key)) return;

  try {
    await fetch(ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        url: payload.url ?? window.location.href,
        userAgent: payload.userAgent ?? navigator.userAgent,
      }),
      // keepalive lets the request complete even if the page is unloading.
      keepalive: true,
    });
  } catch {
    // Swallow — never let the reporter itself break the page.
  }
}

let installed = false;

export function installClientErrorHandlers(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    void reportClientError({
      message: event.message ?? "window.error",
      stack: event.error?.stack ?? null,
      line: event.lineno ?? null,
      column: event.colno ?? null,
      url: event.filename ?? window.location.href,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "Unhandled promise rejection";
    const stack = reason instanceof Error ? reason.stack ?? null : null;
    void reportClientError({ message, stack });
  });
}
